import {defineStore} from 'pinia'
import {dataGet, dataSet, deepMerge} from "../../Common/utils";
import {useLivewireManager} from "../../Registration/stores/livewireManagerStore";
import {useFlowStore} from "../../Registration/stores/flowStore";
import validate from "../../Registration/utils/validate";
import i18n from "../../Common/plugins/i18n";
import {useStepManager} from "../../Registration/stores/stepsManager";
import {useCaptchaManager} from "../../Registration/stores/captchaManagerStore";
import {useSettingsManager} from "../../Registration/stores/settingsManagerStore";
import {useVisibilityRules} from "../../Registration/composables/useVisibilityRules";

const {t} = i18n.global;


export const useBookingStore = defineStore('booking', {
    state: () => ({
        data: {
            companions: [],
        },
        initialBookingData: null,
        runningProcesses:[],
        errors: {},
        _items: [],
        companionItems: [],
    }),
    getters: {
        loading(state) {
            return state.runningProcesses.length > 0;
        },
        status(state) {
            return state?.data?.status ?? null
        },
        items(state) {
            const stepsManager = useStepManager();
            if (stepsManager.steps.length > 0 && stepsManager.current !== undefined) {
                return state._items.filter(item => item.step_id === stepsManager.current.id);
            }
            return state._items;
        }
    },
    actions: {
        initialize(forms, bookingData) {
            const items = forms.main;
            const data = bookingData?.data ?? {}
            this.initialBookingData = bookingData ? JSON.parse(JSON.stringify(bookingData)) : null;
            const guestSettings = ['code', 'status', 'max_companion_limit', 'guest_group_id'];
            guestSettings.forEach(setting => {
                if (data[setting]) {
                    this.data[setting] = data[setting];
                }
            })
            items.forEach(item => {
                dataSet(this.data, item.form_mapping, dataGet(data, item.form_mapping, item.default_value));
            })
            if (bookingData?.data.companions) {
                bookingData.data.companions.forEach(companionData => {
                    this.data.companions.push({...companionData, uniqueId: crypto.randomUUID()})
                })

            }
            this.companionItems = forms.companions;
            this._items = items;
            this.openFirstCompanionIfEnabled();
        },
        openFirstCompanionIfEnabled() {
            const settings = useSettingsManager().settings;
            if (!settings?.withCompanions || !settings?.openFirstCompanionByDefault) {
                return;
            }
            if (!this.companionItems || this.data.companions.length > 0) {
                return;
            }
            const limit = this.data.max_companion_limit ?? settings.companionsLimit;
            if (limit !== null && limit !== undefined && limit < 1) {
                return;
            }
            this.addCompanions();
        },
        startSubmissionLoader() {
            this.startProcessLoader('submission')
        },
        stopSubmissionLoader() {
            this.stopProcessLoader('submission');
        },
        startProcessLoader(process) {
            if (!this.runningProcesses.includes(process)) {
                this.runningProcesses.push(process);
            }
        },
        stopProcessLoader(process) {
            this.runningProcesses = this.runningProcesses.filter(p => p !== process);
        },
        addCompanions() {
            const companion = {uniqueId: crypto.randomUUID()};
            this.companionItems.forEach(item => {
                dataSet(companion, item.form_mapping, item.default_value ?? null);
            })
            this.data.companions.push(companion);
        },
        removeCompanion(uniqueId) {
            this.data.companions = this.data.companions.filter(item => item.uniqueId !== uniqueId);
        },
        checkCaptcha() {
            const captcha = useCaptchaManager();
            const isCaptchaRequired = captcha.required;
            const stepsManager = useStepManager();
            const isMultistepForm = stepsManager.steps.length > 1;
            if (isMultistepForm && !stepsManager.isOnLastStep) {
                return;
            }
            if (isCaptchaRequired) {
                if ([null, ''].includes(captcha.input)) {
                    this.addError('captcha', 'validation.required', {label: "Captcha"})
                } else {
                    this.data.captcha = {'code': captcha.input, 'fingerprint': captcha.captcha.fingerprint};
                }
            } else {
                delete this.data.captcha;
            }

        },
        validate(allItems = false){
            const {evaluateVisibilityRules} = useVisibilityRules();

            this.checkCaptcha()
            // items validation
            const items = allItems ? this._items : this.items;
            items.map(item => {
                if (evaluateVisibilityRules(item.visibility_rules)) {
                    const path = item.form_mapping;
                    validate(this, item, path, dataGet(this.data, path))
                }
            })
            //companions items validation
            this.companionItems.map(item => {
                this.data.companions.forEach((companion, index) => {
                    if (evaluateVisibilityRules(item.visibility_rules, index)) {
                        const path = `companions.${index}.${item.form_mapping}`;
                        validate(this, item, path, dataGet(this.data, path))
                    }
                })
            })
            return this.errors;
        },
        isValid(allItems = false) {
            this.validate(allItems);
            return Object.keys(this.errors).length === 0
        },
        clearErrors(step = null) {
            if (step == null) {
                this.errors = {}
            }
            const stepKeys = this.items.map(item => item.form_mapping);
            stepKeys.forEach(stepKey => {
                if(this.errors[stepKey]){
                    delete this.errors[stepKey];
                }
            })
        },
        async submit(status) {
            const livewireManager = useLivewireManager();
            const flowStore = useFlowStore();
            const stepsManager = useStepManager();
            const captchaManager = useCaptchaManager();
            const LivewireComponent = livewireManager.getComponent();
            try {
                this.clearErrors();
                this.clearInvisibleFieldsData();

                const isValid = this.isValid(true);
                if (!isValid) throw new Error('Registration form - Validation failed');
                const response = await LivewireComponent.call('submit', {...this.data, status})
                if (response.errors) {
                    Object.entries(response.errors).forEach(([key, value]) => {
                        this.errors[key] = value;
                    })
                    return;
                }
                if (response.data) {
                    this.updateData(response.data.guest)
                    flowStore.formSubmissionTransition('success');
                    stepsManager.reset();
                    captchaManager.registerSubmission();
                    if (captchaManager.required) {
                        await captchaManager.generate();
                    }
                }

                this.stopSubmissionLoader();

            } catch (e) {
                this.stopSubmissionLoader();
                console.log(e.message);
            }
        },
        clearInvisibleFieldsData() {
            const {evaluateVisibilityRules} = useVisibilityRules();

            this._items.forEach(item => {
                if (!evaluateVisibilityRules(item.visibility_rules)) {
                    if(dataGet(this.data, item.form_mapping) !== undefined) {
                        dataSet(this.data, item.form_mapping, undefined);
                    }
                    if (this.errors[item.form_mapping]) {
                        delete this.errors[item.form_mapping];
                    }
                }
            });

            // Clear invisible companion fields
            this.companionItems.forEach(item => {
                this.data.companions.forEach((companion, index) => {
                    if (!evaluateVisibilityRules(item.visibility_rules, index)) {
                        const path = `companions.${index}.${item.form_mapping}`;
                        if (dataGet(this.data, path) !== undefined) {
                            dataSet(this.data, path, undefined);
                        }
                        // Clear errors
                        if (this.errors[path]) {
                            delete this.errors[path];
                        }
                    }
                });
            });
        },
        updateField(path, value) {
            dataSet(this.data, path, value);
            this.clearInvisibleFieldsData();
        },
        updateData(value) {
            deepMerge(this.data, value);
        },
        addError(path, error, item = null) {
            this.errors[path] = [];
            this.errors[path].push(t(error, {attribute: item ? item.label : ''}));
        }
    }
})
